import { DestroyRef, Directive, DOCUMENT, ElementRef, HostListener, inject, input, Renderer2 } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { WA_WINDOW } from '@ng-web-apis/common';
import { timer } from 'rxjs';

import { BUFFER_OFFSET, TOOLTIP_ACTION_STATUS, TOOLTIP_FADE_DELAY, TOOLTIP_POSITION } from './tooltip.consts';

@Directive({
  selector: '[mdcrTooltip]',
})
export class TooltipDirective {
  private readonly renderer = inject(Renderer2);
  private readonly document = inject(DOCUMENT);
  private readonly window = inject(WA_WINDOW);
  private readonly destroyRef = inject(DestroyRef);
  private readonly element = inject(ElementRef);

  public readonly message = input.required<string>();
  public readonly messageOnClick = input<string>();
  public readonly changeStatusOnClick = input.required<boolean>();
  public readonly position = input.required<string>();

  private tooltip!: HTMLElement | null;
  private messageToShow!: HTMLElement | null;
  private isSubmitted = false;

  @HostListener('mouseenter') public onMouseEnter(): void {
    if (this.isSubmitted) {
      this.isSubmitted = false;
      if (this.tooltip) {
        this.hide();
      }
    }
    this.show();
  }

  @HostListener('mouseleave') public onMouseLeave(): void {
    if (this.tooltip) {
      this.hide();
    }
  }

  @HostListener('click') public onClick(): void {
    if (this.changeStatusOnClick()) {
      this.renderer.removeChild(this.tooltip, this.messageToShow);
      this.messageToShow = this.renderer.createText(this.messageOnClick() ?? '');
      this.renderer.appendChild(this.tooltip, this.messageToShow);

      this.renderer.removeClass(this.tooltip, `tooltip--${TOOLTIP_ACTION_STATUS.waiting}`);
      this.renderer.addClass(this.tooltip, `tooltip--${TOOLTIP_ACTION_STATUS.submitted}`);

      this.isSubmitted = true;
    }
  }

  private show(): void {
    this.create();
    this.setPosition();
    this.renderer.addClass(this.tooltip, 'tooltip--show');
  }

  private hide(): void {
    if (this.isSubmitted) {
      this.renderer.addClass(this.tooltip, 'tooltip--fade');
      this.renderer.removeClass(this.tooltip, 'tooltip--show');
      timer(TOOLTIP_FADE_DELAY)
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe(() => {
          if (this.tooltip) {
            this.renderer.removeChild(this.document.body, this.tooltip);
            this.tooltip = null;
          }
        });
    } else {
      this.renderer.removeClass(this.tooltip, 'tooltip--show');
      this.renderer.removeChild(this.document.body, this.tooltip);
      this.tooltip = null;
    }
  }

  private create(): void {
    this.tooltip = this.renderer.createElement('span');
    this.messageToShow = this.renderer.createText(this.message());

    this.renderer.appendChild(this.tooltip, this.messageToShow);
    this.renderer.appendChild(this.document.body, this.tooltip);

    this.renderer.addClass(this.tooltip, 'tooltip');
    this.renderer.addClass(this.tooltip, `tooltip--${TOOLTIP_ACTION_STATUS.waiting}`);
  }

  private setPosition(): void {
    const hostPosition = this.element.nativeElement.getBoundingClientRect();
    const tooltipPosition = this.tooltip!.getBoundingClientRect();
    const scrollPosition = this.window.scrollY;

    let top, left;

    if (this.position() === TOOLTIP_POSITION.top) {
      top = hostPosition.top - tooltipPosition.height - BUFFER_OFFSET;
      left = hostPosition.left + (hostPosition.width - tooltipPosition.width) / 2;
    }

    if (this.position() === TOOLTIP_POSITION.bottom) {
      top = hostPosition.bottom + BUFFER_OFFSET;
      left = hostPosition.left + (hostPosition.width - tooltipPosition.width) / 2;
    }

    if (this.position() === TOOLTIP_POSITION.left) {
      top = hostPosition.top + (hostPosition.height - tooltipPosition.height) / 2;
      left = hostPosition.left - tooltipPosition.width - BUFFER_OFFSET;
    }

    if (this.position() === TOOLTIP_POSITION.right) {
      top = hostPosition.top + (hostPosition.height - tooltipPosition.height) / 2;
      left = hostPosition.right + BUFFER_OFFSET;
    }

    this.renderer.setStyle(this.tooltip, 'top', `${top + scrollPosition}px`);
    this.renderer.setStyle(this.tooltip, 'left', `${left}px`);
  }
}
